import { baseApi, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { onecEnvelopeDecoder, type OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { writeCommandBody, type WriteCommandIdentity } from '../../../shared/api/writeCommand'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import type { InventorySession, ScanResult, SessionRow } from '../model/types'
import {
  ONEC_INVENTORY_SCHEMAS,
  decodeOptionalSession,
  decodeScan,
  decodeSession,
  rowToWire,
} from './contracts'
import { ONEC_INVENTORY_URLS } from './urls'

/**
 * Endpoints инвентаризации 1С в ОБЩЕМ `baseApi` (R04): имена `onec*`, rid —
 * аргумент каждого запроса (значит, часть ключа кэша) и часть каждого тега,
 * склад — часть ключа сессии. Декодер сверяет rid конверта с запрошенным.
 */
export const ONEC_INVENTORY_TAG = 'OneCInventory' as const

export interface InventoryResourceArgs {
  resourceId: string
}

export interface InventorySessionArgs {
  resourceId: string
  warehouseRefKey: string
}

export interface InventoryScanArgs extends InventorySessionArgs {
  barcode: string
}

interface WriteArgs<T> {
  resourceId: string
  identity: WriteCommandIdentity
  data: T
}

export type InventoryCreateArgs = WriteArgs<{
  name: string
  barcode: string
  prices: readonly { warehouseRefKey: string; price: number }[]
}>

export type InventorySaveArgs = WriteArgs<{
  warehouseRefKey: string
  warehouseName: string
  rows: readonly SessionRow[]
}>

export interface InventoryClearArgs extends WriteArgs<null> {
  warehouseRefKey: string
}

export const sessionTag = (resourceId: string, warehouseRefKey: string) =>
  resourceTag(ONEC_INVENTORY_TAG, normalizeRid(resourceId), `SESSION:${warehouseRefKey.trim().toLowerCase()}`)

export const inventoryApi = baseApi.enhanceEndpoints({ addTagTypes: [ONEC_INVENTORY_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    /** Сессия склада с сервера: возобновление после F5 и на другом рабочем месте. Нет сессии — `data: null`. */
    onecInventorySession: build.query<OneCEnvelope<InventorySession | null>, InventorySessionArgs>({
      query: ({ resourceId, warehouseRefKey }) => ({
        url: ONEC_INVENTORY_URLS.session(resourceId),
        params: { warehouseRefKey },
        decode: onecEnvelopeDecoder(resourceId, decodeOptionalSession),
      }),
      providesTags: (_result, _error, { resourceId, warehouseRefKey }) => [
        sessionTag(resourceId, warehouseRefKey),
        resourceScopeTag(normalizeRid(resourceId)),
      ],
    }),
    /** Скан — живое чтение 1С этой базы (GET, безопасен для повтора). */
    onecInventoryScan: build.query<OneCEnvelope<ScanResult>, InventoryScanArgs>({
      query: ({ resourceId, warehouseRefKey, barcode }) => ({
        url: ONEC_INVENTORY_URLS.scan(resourceId),
        params: { barcode, warehouseRefKey },
        decode: onecEnvelopeDecoder(resourceId, decodeScan),
      }),
      providesTags: (_result, _error, { resourceId }) => [resourceScopeTag(normalizeRid(resourceId))],
    }),

    // --- команды: ключ и operationId приходят от операции, не создаются здесь ---
    /** Создание товара в 1С (карточка → штрихкод → цены складов). Исход неизвестен — повтор только тем же ключом. */
    onecInventoryCreateProduct: build.mutation<OneCEnvelope<ScanResult>, InventoryCreateArgs>({
      query: ({ resourceId, identity, data }) => ({
        url: ONEC_INVENTORY_URLS.product(resourceId),
        method: 'POST',
        body: writeCommandBody(ONEC_INVENTORY_SCHEMAS.create, identity, data),
        idempotencyKey: identity.idempotencyKey,
        decode: onecEnvelopeDecoder(resourceId, decodeScan),
      }),
    }),
    /** Сохранение набора строк целиком: «последний прислал — то и есть». Ответ — сохранённая сессия. */
    onecInventorySaveSession: build.mutation<OneCEnvelope<InventorySession>, InventorySaveArgs>({
      query: ({ resourceId, identity, data }) => ({
        url: ONEC_INVENTORY_URLS.session(resourceId),
        method: 'PUT',
        body: writeCommandBody(ONEC_INVENTORY_SCHEMAS.saveSession, identity, {
          warehouseRefKey: data.warehouseRefKey,
          warehouseName: data.warehouseName,
          rows: data.rows.map(rowToWire),
        }),
        idempotencyKey: identity.idempotencyKey,
        decode: onecEnvelopeDecoder(resourceId, decodeSession),
      }),
    }),
    /** Очистка локальной сессии склада: в 1С ничего не удаляется. */
    onecInventoryClearSession: build.mutation<null, InventoryClearArgs>({
      query: ({ resourceId, warehouseRefKey, identity }) => ({
        url: ONEC_INVENTORY_URLS.session(resourceId),
        method: 'DELETE',
        params: { warehouseRefKey },
        body: writeCommandBody(ONEC_INVENTORY_SCHEMAS.clearSession, identity, null),
        idempotencyKey: identity.idempotencyKey,
      }),
    }),
  }),
})

export const {
  onecInventorySession,
  onecInventoryScan,
  onecInventoryCreateProduct,
  onecInventorySaveSession,
  onecInventoryClearSession,
} = inventoryApi.endpoints
